import { usePostHog } from '@posthog/react'
import { useTranslation } from 'react-i18next'
import { useCart } from '@/hooks/use-cart'
import { useCredit } from '@/hooks'
import type { NewItem } from '@/types/new-item'
import { useAppDispatch, useAppSelector } from '@/redux/hooks'
import { QuantitySelector } from '@/ui/components/quantity-selector'
import { removeReturnedItem } from '@/redux/return/return-slice'
import { TrashIcon } from '@/ui/icons/trash'
import { MediaImageIcon } from '@/ui/icons/media-image'

interface CartItemProps {
  item: NewItem
  currentReturnedItemId: string
}

export const CartItem = ({ item, currentReturnedItemId }: CartItemProps) => {
  const posthog = usePostHog()
  const {
    i18n: { language }
  } = useTranslation()

  const checkoutId = useAppSelector(
    (store) => store.returnsApi.order.response?.checkout_id
  )

  const dispatch = useAppDispatch()

  const ecommerceGroup = useAppSelector(
    (store) => store.settingsApi.settings.response.ecommerce_group
  )

  const newItems = useAppSelector((state) => state.return.newItems)
  const returnedItems = useAppSelector((state) => state.return.returnedItems)

  const { promotionalUnitsForCartItem, cartItemTotalPrice } = useCredit()

  // Only a partly covered line has no unit price to show; then give what it costs in full
  const promotionalUnits = promotionalUnitsForCartItem(item)
  const lineTotal = cartItemTotalPrice(item)
  const price =
    promotionalUnits === 0
      ? item.price
      : promotionalUnits >= item.quantity
        ? lineTotal / item.quantity
        : lineTotal

  const formattedPrice = item.currencyCode
    ? price.toLocaleString(language, {
        style: 'currency',
        currency: item.currencyCode
      })
    : null

  const { removeCartItem, updateCartItemQuantity } = useCart()

  const onUpdateItemQuantity = (quantity: number) => {
    updateCartItemQuantity(item, quantity)
  }

  const onRemoveItem = () => {
    posthog.capture('FC - Item deleted from cart', {
      checkout_id: checkoutId
    })

    removeCartItem(item)

    if (item.returnedItemId !== currentReturnedItemId) {
      const associatedItems = newItems.filter(
        (i) => i.returnedItemId === item.returnedItemId
      )
      if (associatedItems.length <= 1) {
        const returnedItem = returnedItems.find(
          (returnedItem) => returnedItem.lineItemId === item.returnedItemId
        )
        if (returnedItem) {
          dispatch(
            removeReturnedItem({
              lineItemId: returnedItem.lineItemId
            })
          )
        }
      }
    }
  }

  const isMisako = ecommerceGroup === 'misako'
  const isHoff = ecommerceGroup === 'hoff'

  if (isHoff) {
    return (
      <div className="flex items-center gap-4 bg-white py-3">
        <div className="h-20 w-16">
          <img
            src={item.productVariant.imageUrl}
            height={80}
            width={64}
            alt={item.productVariant.variantName}
            className="size-full object-cover"
          />
        </div>
        <div className="flex flex-1 justify-between gap-4">
          <div>
            <h3 className="text-base font-medium text-neutral-900">
              {item.productVariant.productName}
            </h3>
            <p className="text-sm font-light text-neutral-600">
              {item.productVariant.variantName}
            </p>
            <p className="text-sm font-medium text-neutral-900">
              {formattedPrice}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <QuantitySelector
              value={item.quantity}
              onValueChange={onUpdateItemQuantity}
              minValue={1}
              maxValue={item.productVariant.inventoryQuantity ?? 1}
            />
            <button
              className="rounded-md p-1.5 hover:bg-neutral-100"
              onClick={onRemoveItem}
              aria-label="Remove product from cart"
            >
              <TrashIcon width={16} height={16} className="text-neutral-600" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  if (isMisako) {
    return (
      <div className="flex items-center gap-4 bg-white py-3">
        <div className="size-16">
          <img
            src={item.productVariant.imageUrl}
            height={64}
            width={64}
            alt={item.productVariant.variantName}
            className="size-full object-cover"
          />
        </div>
        <div className="flex flex-1 justify-between gap-4">
          <div>
            <h3 className="text-base font-light text-neutral-900">
              {item.productVariant.productName}
            </h3>
            <p className="text-sm font-light text-neutral-600">
              {item.productVariant.variantName}
            </p>
            <p className="text-sm font-medium text-neutral-900">
              {formattedPrice}
            </p>
          </div>
          <div className="flex items-center gap-4">
            <QuantitySelector
              value={item.quantity}
              onValueChange={onUpdateItemQuantity}
              minValue={1}
              maxValue={item.productVariant.inventoryQuantity ?? 1}
            />
            <button
              className="rounded-md p-1.5 hover:bg-neutral-100"
              onClick={onRemoveItem}
              aria-label="Remove product from cart"
            >
              <TrashIcon width={16} height={16} className="text-neutral-600" />
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-1 rounded-lg bg-white shadow-sm">
      <div className="size-16 scale-110 overflow-hidden rounded-xl shadow-md transition-transform hover:!rotate-0 group-odd:-rotate-2 group-even:rotate-2">
        {item.productVariant.imageUrl ? (
          <img
            src={item.productVariant.imageUrl}
            height={64}
            width={64}
            alt={item.productVariant.variantName}
            className="size-full object-cover"
          />
        ) : (
          <div className="flex size-full min-h-0 items-center justify-center bg-neutral-50 text-neutral-500">
            <MediaImageIcon width={14} height={14} />
          </div>
        )}
      </div>
      <div className="flex flex-1 justify-between gap-4 px-4 py-3">
        <div>
          <h3 className="line-clamp-1 text-sm font-semibold text-neutral-900 md:text-base">
            {item.productVariant.productName}
          </h3>
          <p className="text-sm font-light text-neutral-600">
            {item.productVariant.variantName}
          </p>
          <p className="text-sm font-medium text-neutral-900">
            {formattedPrice}
          </p>
        </div>
        <div className="flex items-center gap-4">
          <QuantitySelector
            value={item.quantity}
            onValueChange={onUpdateItemQuantity}
            minValue={1}
            maxValue={item.productVariant.inventoryQuantity ?? 1}
          />
          <button
            className="rounded-md p-1.5 hover:bg-neutral-100"
            onClick={onRemoveItem}
            aria-label="Remove product from cart"
          >
            <TrashIcon width={16} height={16} className="text-neutral-600" />
          </button>
        </div>
      </div>
    </div>
  )
}
